import React, { useState } from 'react'
import { useAuthViewModel } from '../hooks/useAuthViewModel'
import { theme } from '../../../core/design-system/theme'

export default function AuthView({ authService }) {
  const vm = useAuthViewModel(authService)
  const [passwordRevealed, setPasswordRevealed] = useState(false)

  const inputStyle = {
    width: '100%',
    background: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.layout.inputRadius,
    padding: '13px 16px',
    fontSize: 15,
    fontFamily: 'var(--font-body)',
    color: theme.colors.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  }

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.colors.textPrimary,
    marginBottom: 6,
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.cream, display: 'flex', flexDirection: 'column' }}>
      {/* Hero strip */}
      <div
        style={{
          background: `linear-gradient(140deg, ${theme.colors.terracotta} 0%, ${theme.colors.terracottaLight} 100%)`,
          padding: '56px 24px 40px',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 36,
            fontWeight: 600,
            color: '#fff',
            margin: '0 0 10px',
          }}
        >
          Nonna
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16, margin: 0, lineHeight: 1.6 }}>
          An extra set of trusted hands so parents can enjoy time with their family.
        </p>
      </div>

      {/* Form card */}
      <div
        style={{
          flex: 1,
          maxWidth: 440,
          width: '100%',
          margin: '0 auto',
          padding: '32px 24px 40px',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            fontWeight: 500,
            color: theme.colors.textPrimary,
            margin: '0 0 24px',
          }}
        >
          {vm.mode === 'signIn' ? 'Welcome back' : 'Create your account'}
        </h2>

        {vm.mode === 'signUp' && (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Full name</label>
            <input
              style={inputStyle}
              type="text"
              placeholder="Maria Garcia"
              value={vm.fullName}
              onChange={(e) => vm.setFullName(e.target.value)}
              autoComplete="name"
            />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            type="email"
            placeholder="you@example.com"
            value={vm.email}
            onChange={(e) => vm.setEmail(e.target.value)}
            autoComplete="email"
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 44 }}
              type={passwordRevealed ? 'text' : 'password'}
              placeholder="••••••••"
              value={vm.password}
              onChange={(e) => vm.setPassword(e.target.value)}
              autoComplete={vm.mode === 'signIn' ? 'current-password' : 'new-password'}
            />
            <button
              type="button"
              onClick={() => setPasswordRevealed((r) => !r)}
              aria-label={passwordRevealed ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
            >
              {passwordRevealed ? (
                /* eye-slash */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                /* eye */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {vm.status === 'error' && (
          <div
            style={{
              background: theme.colors.errorBg,
              color: theme.colors.errorText,
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
              marginBottom: 16,
            }}
            role="alert"
          >
            {vm.errorMessage}
          </div>
        )}

        <button
          onClick={vm.submit}
          disabled={vm.status === 'loading'}
          style={{
            width: '100%',
            background: theme.colors.terracotta,
            color: '#fff',
            border: 'none',
            borderRadius: theme.layout.buttonRadius,
            padding: '14px 0',
            fontSize: 16,
            fontWeight: 700,
            fontFamily: 'var(--font-body)',
            cursor: vm.status === 'loading' ? 'not-allowed' : 'pointer',
            opacity: vm.status === 'loading' ? 0.7 : 1,
            transition: 'all 200ms ease',
            boxShadow: `0 2px 8px ${theme.colors.terracotta}55`,
            marginBottom: 16,
          }}
          aria-label={vm.mode === 'signIn' ? 'Sign in' : 'Create account'}
        >
          {vm.status === 'loading' ? 'Please wait…' : vm.mode === 'signIn' ? 'Sign in' : 'Create account'}
        </button>

        <button
          onClick={vm.switchMode}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.terracotta,
            fontSize: 14,
            cursor: 'pointer',
            width: '100%',
            textAlign: 'center',
            padding: '8px 0',
            fontFamily: 'var(--font-body)',
          }}
        >
          {vm.mode === 'signIn'
            ? "Don't have an account? Sign up"
            : 'Already have an account? Sign in'}
        </button>
      </div>
    </div>
  )
}
